import React, { useState, useEffect } from "react";
import Layout from "../../layouts";
import DynamicTable from "../../components/DynamicTable";
import apiService from "../../services/apiService";
import { toast } from "react-toastify";
import { formatDateTimeAmORPm } from "../../utils/common";
import { withSearchIcon } from "../../utils/helper";
import { Crown } from "lucide-react";
import { FaFileCsv } from "react-icons/fa";

interface User {
  id: number;
  created_at?: string;
  updated_at?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  phone_no?: string;
  gender?: string;
  dob?: string;
  profile_photo?: string;
  occupation?: string;
  height?: string;
  country?: string;
  city?: string;
  last_seen?: string;
  active?: boolean;
  visible?: boolean;
  last_visited?: string;
  blocked?: boolean;
  blocker_id?: number;
  blocker_type?: string;
  profile_status?: string;
  is_logged_in?: boolean;
  login_source?: string;
  meeting_purpose?: string;
  interested_in_gender?: string;
  [key: string]: any;
}

interface UserEventActivity {
  eventId: number;
  eventName: string;
  rsvpStatus: string;
  totalTickets: number;
  refundedTickets: number;
  purchasedAmount: number;
  refundedAmount: number;
  sochaFee: number;
  isHost?: boolean;
}

export default function UsersList() {
  const [page, setPage] = useState(1);
  const [users, setUsers] = useState<User[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);

  const [confirmModal, setConfirmModal] = useState<{
    open: boolean;
    userId?: number;
    action?: "block" | "unblock";
  }>({ open: false });

  const [processing, setProcessing] = useState(false);

  // 🟢 New: Search State
  const [searchTerm, setSearchTerm] = useState("");
  const [activeUserTab, setActiveUserTab] = useState("Personal Details");
  const [exportingCSV, setExportingCSV] = useState(false);

  const [eventActivity, setEventActivity] = useState<UserEventActivity[]>([]);
  const [eventActivityLoading, setEventActivityLoading] = useState(false);
  const [eventActivityPage, setEventActivityPage] = useState(1);
  const [eventActivityTotalPages, setEventActivityTotalPages] = useState(1);
  const [eventActivityTotalCount, setEventActivityTotalCount] = useState(0);
  const [eventActivityExporting, setEventActivityExporting] = useState(false);
  const eventActivityLimit = 25;

  useEffect(() => {
    setActiveUserTab("Personal Details");
  }, []);

  const [viewModal, setViewModal] = useState<{
    open: boolean;
    user?: User;
  }>({ open: false });

  useEffect(() => {
    fetchUsers(page, searchTerm);
  }, [page, searchTerm]); // 🟢 refetch when search changes

  // ✅ Fetch Users
  const fetchUsers = async (activePageNo: number, search: string = "") => {
    try {
      setLoading(true);
      const params = { page: activePageNo, limit: 50, search }; // 🟢 send search to API
      const response: any = await apiService.get("/users/get-users", params);
      setUsers(response?.data?.users || []);
      setTotalRecords(response?.data?.totalUserCount || 0);
      setTotalPages(response?.data?.totalPages || 1);
    } catch (error) {
      console.error("Error fetching users:", error);
      toast.error("Failed to fetch users");
    } finally {
      setLoading(false);
    }
  };

  // ✅ Open modal before API call
  const handleToggleBlock = (id: number, blocked: boolean) => {
    setConfirmModal({
      open: true,
      userId: id,
      action: blocked ? "unblock" : "block",
    });
  };

  // ✅ Confirm and call Block/Unblock API
  const handleConfirmAction = async () => {
    if (!confirmModal.userId || !confirmModal.action) return;

    const endpoint =
      confirmModal.action === "block"
        ? "/users/block-user"
        : "/users/unblock-user";

    const apiKey =
      confirmModal.action === "block"
        ? "CE373CK6-DD47-41CF-04BF-D992A4206NA7"
        : "CH764A2A-3EU8-482F-AC72-17D04961DJC0";

    try {
      setProcessing(true);
      await apiService.put(endpoint, { userId: confirmModal.userId });

      toast.success(
        confirmModal.action === "block"
          ? "User blocked successfully"
          : "User unblocked successfully"
      );

      // ✅ Update UI instantly
      setUsers((prev) =>
        prev.map((u) =>
          u.id === confirmModal.userId
            ? { ...u, blocked: confirmModal.action === "block" }
            : u
        )
      );

      setConfirmModal({ open: false });
    } catch (error) {
      console.error("Error during block/unblock:", error);
      toast.error("Failed to update user status");
    } finally {
      setProcessing(false);
    }
  };

  // ✅ Table Columns
  const columns = [
    { key: "id", label: "User ID" },
    { key: "first_name", label: withSearchIcon("First Name") },
    { key: "last_name", label: withSearchIcon("Last Name") },
    {
      key: "host_alias",
      label: withSearchIcon("Organization Name"),
      render: (val: string) => (val ? val : "N/A"),
    },
    { key: "email", label: withSearchIcon("Email") },
    { key: "phone_no", label: "Phone No" },
    {
      key: "gender",
      label: "Gender",
      render: (val: string) => {
        let badgeClass = "px-2 py-1 rounded-full text-xs font-semibold ";
        badgeClass +=
          val === "Men"
            ? "bg-blue-100 text-blue-800"
            : val === "Women"
            ? "bg-pink-100 text-pink-800"
            : val === "Non-Binary"
            ? "bg-purple-100 text-purple-800"
            : "bg-gray-100 text-gray-800";
        return <span className={badgeClass}>{val || "N/A"}</span>;
      },
    },
    {
      key: "profile_photo",
      label: "Profile Photo",
      render: (val: string) =>
        val ? (
          <div className="flex items-center gap-2">
            <img
              src={val}
              alt="Profile"
              className="w-10 h-10 rounded-full object-cover border"
            />
            <span
              onClick={() => {
                navigator.clipboard.writeText(val);
                toast.success("Image URL copied!");
              }}
              className="text-xs text-blue-500 hover:underline cursor-pointer"
            >
              Copy URL
            </span>
          </div>
        ) : (
          "N/A"
        ),
    },

    {
      key: "blocked",
      label: "Status",
      render: (val: boolean) => {
        let badgeClass = "px-2 py-1 rounded-full text-xs font-semibold ";
        badgeClass += val === true
          ? "bg-red-100 text-red-800"
          : "bg-green-100 text-green-800";
        return <span className={badgeClass}>{val === true ? "Blocked" : "Active"}</span>;
      },
    },
    {
      key: "actions",
      label: "Actions",
      className:
        "px-4 py-3 whitespace-nowrap text-right sticky right-0 z-10 rounded space-x-2 bg-white/30 backdrop-blur-md shadow-md",
      render: (_: any, row: User) => (
        <div className="flex gap-2">
          {/* 👁‍🗨 View Button */}
          <button
            onClick={() => {
              openViewModal(row);
            }}
            className="px-2 py-1 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded w-[4rem]"
          >
            View
          </button>

          {/* 📅 Events Button */}
          <button
            onClick={() => openEventActivityModal(row)}
            className="px-2 py-1 text-xs bg-indigo-600 hover:bg-indigo-700 text-white rounded whitespace-nowrap"
          >
            Event Activity
          </button>

          {/* Block / Unblock */}
          <button
            onClick={() => handleToggleBlock(row.id, row.blocked!)}
            disabled={processing}
            className={`px-2 py-1 text-xs w-[4rem] text-white rounded ${
              row.blocked
                ? "bg-[#3AB795]/90 hover:bg-[#3AB795]/100"
                : "bg-[#E75A5A]/90 hover:bg-[#E75A5A]/100"
            }`}
          >
            {row.blocked ? "Unblock" : "Block"}
          </button>
        </div>
      ),
    },
  ];

  const resetEventActivity = () => {
    setEventActivity([]);
    setEventActivityPage(1);
    setEventActivityTotalPages(1);
    setEventActivityTotalCount(0);
  };

  const openViewModal = (user: User) => {
    resetEventActivity();
    setViewModal({ open: true, user });
    setActiveUserTab("Personal Info");
  };

  const openEventActivityModal = (user: User) => {
    resetEventActivity();
    setViewModal({ open: true, user });
    setActiveUserTab("Event Activity");
  };

  const fetchUserEventActivity = async (userId: number, pageNo: number) => {
    try {
      setEventActivityLoading(true);
      const response: any = await apiService.get(
        "/users/events/get-user-event-activity",
        { userId, page: pageNo, limit: eventActivityLimit }
      );
      setEventActivity(response?.data?.data || []);
      setEventActivityTotalPages(response?.data?.totalPages || 1);
      setEventActivityTotalCount(response?.data?.totalCount || 0);
    } catch (error) {
      console.error("Error fetching user event activity:", error);
      toast.error("Failed to fetch event activity");
    } finally {
      setEventActivityLoading(false);
    }
  };

  useEffect(() => {
    if (
      viewModal.open &&
      viewModal.user?.id &&
      activeUserTab === "Event Activity"
    ) {
      fetchUserEventActivity(viewModal.user.id, eventActivityPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewModal.open, viewModal.user?.id, activeUserTab, eventActivityPage]);

  const handleExportUserEventActivityCSV = async () => {
    if (!viewModal.user?.id) return;
    try {
      setEventActivityExporting(true);
      const res: any = await apiService.get(
        "/export/export-user-event-activity",
        { userId: viewModal.user.id },
        { responseType: "blob" }
      );

      if (res) {
        const blob =
          res instanceof Blob
            ? res
            : new Blob([res], { type: "text/csv;charset=utf-8;" });

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `user_${viewModal.user.id}_event_activity.csv`
        );
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        toast.success("Event activity CSV exported successfully.");
      }
    } catch (error: any) {
      console.error("Export error:", error);
      toast.error("Failed to export event activity CSV.");
    } finally {
      setEventActivityExporting(false);
    }
  };

  const handleExportUsersCSV = async () => {
    try {
      setExportingCSV(true);

      const res: any = await apiService.get(
        "/export/export-user-event-stats",
        undefined,
        { responseType: "blob" }
      );

      if (res) {
        const blob =
          res instanceof Blob
            ? res
            : new Blob([res], { type: "text/csv;charset=utf-8;" });

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;

        link.setAttribute("download", `users_export.csv`);

        document.body.appendChild(link);
        link.click();

        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        toast.success("Users CSV exported successfully.");
      }
    } catch (error: any) {
      console.error("Export error:", error);
      toast.error("Failed to export CSV. Please try again.");
    } finally {
      setExportingCSV(false);
    }
  };

  return (
    <Layout pageTitle="Users">
      <div className="min-h-screen bg-gray-50 py-3 px-4 min-w-0">
        <DynamicTable
          data={users}
          columns={columns}
          pageSize={50}
          loading={loading}
          title="Users"
          activeNUmber={page}
          onPageChange={(newPage) => setPage(newPage)}
          totalRecords={totalRecords}
          totalPages={totalPages}
          onSearch={(value: string) => {
            // 🟢 Listen to DynamicTable search input
            setPage(1); // reset to first page
            setSearchTerm(value); // update search key
          }}
          exportTypes={["csv"]}
          handleExportEventsCSV={handleExportUsersCSV}
          exportLoading={exportingCSV}
          exportCsvTitle="Export Users CSV"
        />
      </div>

      {/* ✅ Confirmation Modal */}
      {confirmModal.open && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg w-[90%] max-w-sm p-6">
            <h2 className="text-lg font-semibold mb-4">
              {confirmModal.action === "block"
                ? "Confirm Block User"
                : "Confirm Unblock User"}
            </h2>
            <p className="text-sm text-gray-600 mb-6">
              Are you sure you want to{" "}
              <strong>
                {confirmModal.action === "block" ? "block" : "unblock"}
              </strong>{" "}
              this user?
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setConfirmModal({ open: false })}
                className="px-4 py-2 text-sm rounded border border-gray-300 hover:bg-gray-100"
                disabled={processing}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmAction}
                disabled={processing}
                className={`px-4 py-2 text-sm rounded text-white ${
                  confirmModal.action === "block"
                    ? "bg-red-600 hover:bg-red-700"
                    : "bg-green-600 hover:bg-green-700"
                }`}
              >
                {processing ? "Processing..." : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* VIEW USER DETAILS MODAL */}
      {viewModal.open && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          {/* <div className="bg-white w-[95%] max-w-3xl rounded-lg shadow-xl p-6 max-h-[90vh] overflow-y-auto"> */}
          <div className="w-[auto] w-[75vw] bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold mb-4">User Details</h2>

              <button
                onClick={() => setViewModal({ open: false })}
                className="w-8 h-8 flex items-center justify-center 
               rounded-full hover:bg-red-200 
               transition text-red-600 hover:text-red-900"
              >
                ✕
              </button>
            </div>
            {/* Tabs */}
            <div className="flex space-x-2 border-b mb-4 overflow-x-auto">
              {[
                "Personal Info",
                "Organization Info",
                "Connection Preferences",
                "Platform Activity",
                "Event Activity",
              ].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveUserTab(tab)}
                  className={`px-4 py-2 text-sm font-medium whitespace-nowrap ${
                    activeUserTab === tab
                      ? "border-b-2 border-blue-600 text-blue-600"
                      : "text-gray-600 hover:text-blue-600"
                  }`}
                >
                  {tab}
                </button>
              ))}
            </div>

            {/* TAB CONTENT */}
            <div className="text-sm text-gray-800 space-y-4">
              {/* 🟦 PERSONAL INFO TAB */}
              {activeUserTab === "Personal Info" && (
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 space-y-4">
                  <h3 className="text-base font-semibold text-blue-700 border-l-4 border-blue-500 pl-2">
                    Personal Information
                  </h3>

                  <div className="grid grid-cols-3 gap-4 items-start">
                    {/* Left Side — Text Fields */}
                    <div className="col-span-2 grid grid-cols-2 gap-3">
                      <p>
                        <strong>Name:</strong> {viewModal.user?.first_name}{" "}
                        {viewModal.user?.last_name}
                      </p>
                      <p>
                        <strong>Email:</strong> {viewModal.user?.email}
                      </p>
                      <p>
                        <strong>Phone:</strong> {viewModal.user?.phone_no}
                      </p>
                      <p>
                        <strong>Gender:</strong> {viewModal.user?.gender}
                      </p>
                      <p>
                        <strong>DOB:</strong> {viewModal.user?.dob}
                      </p>
                      <p>
                        <strong>City:</strong> {viewModal.user?.city}
                      </p>
                      <p>
                        <strong>Country:</strong> {viewModal.user?.country}
                      </p>
                      <p>
                        <strong>Height:</strong>{" "}
                        {viewModal.user?.height || "N/A"}
                      </p>
                      <p>
                        <strong>Occupation:</strong>{" "}
                        {viewModal.user?.job_title || viewModal.user?.workplace
                          ? `${viewModal.user?.job_title || ""}${
                              viewModal.user?.job_title &&
                              viewModal.user?.workplace
                                ? " at "
                                : ""
                            }${viewModal.user?.workplace || ""}`
                          : "N/A"}
                      </p>
                    </div>

                    {/* Right Side — Profile Photo */}
                    <div className="flex flex-col items-center gap-2">
                      <img
                        src={
                          viewModal.user?.profile_photo || "/default-avatar.png"
                        }
                        alt="Profile"
                        className="w-32 h-32 rounded-xl object-cover shadow-md border border-gray-300"
                      />

                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(
                            viewModal.user?.profile_photo || ""
                          );
                          toast.success("Profile image URL copied!");
                        }}
                        className="text-xs text-blue-600 underline hover:text-blue-800"
                      >
                        Copy URL
                      </button>
                    </div>
                  </div>

                  {/* STATUS BLOCK */}
                  <div className="mt-3 space-y-2">
                    <h4 className="text-sm font-semibold text-blue-700">
                      Status
                    </h4>

                    <div className="flex items-center gap-3 bg-white border border-blue-200 p-3 rounded-lg">
                      <span
                        className={`
            w-3 h-3 rounded-full 
            ${
              !viewModal.user?.blocked
                ? "bg-green-500"
                : viewModal.user?.blocked
                ? "bg-red-500"
                : "bg-gray-400"
            }
          `}
                      />

                      <span
                        className={`
            text-xs font-medium px-3 py-1 rounded-md
            ${
              !viewModal.user?.blocked
                ? "bg-green-100 text-green-700"
                : viewModal.user?.blocked
                ? "bg-red-100 text-red-700"
                : "bg-gray-100 text-gray-700"
            }
          `}
                      >
                        {!viewModal.user?.blocked
                          ? "Active User"
                          : viewModal.user?.blocked
                          ? "Blocked User"
                          : "Inactive"}
                      </span>
                    </div>

                    {/* Profile Completion */}
                    <div className="bg-white border border-blue-200 p-3 rounded-lg">
                      <p>
                        <strong>Profile Status:</strong>{" "}
                        {viewModal.user?.profile_status || "Incomplete"}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* 🟩 ORGANIZATION TAB */}
              {activeUserTab === "Organization Info" && (
                <div className="bg-green-50 border border-green-200 rounded-xl p-4 space-y-3">
                  <h3 className="text-base font-semibold text-green-700 border-l-4 border-green-500 pl-2">
                    Organization Details
                  </h3>

                  <p>
                    <strong>Org Name:</strong>{" "}
                    {viewModal.user?.host_alias || "N/A"}
                  </p>
                </div>
              )}

              {/* 🟨 CONNECTION PREFERENCES TAB */}
              {activeUserTab === "Connection Preferences" && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 space-y-3">
                  <h3 className="text-base font-semibold text-yellow-700 border-l-4 border-yellow-500 pl-2">
                    Connection Preferences
                  </h3>

                  <p>
                    <strong>Purpose:</strong>{" "}
                    {viewModal.user?.meeting_purpose || "N/A"}
                  </p>
                  <p>
                    <strong>Interested In:</strong>{" "}
                    {viewModal.user?.interested_in_gender || "N/A"}
                  </p>
                </div>
              )}

              {/* 🟪 PLATFORM ACTIVITY TAB */}
              {activeUserTab === "Platform Activity" && (
                <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 space-y-3">
                  <h3 className="text-base font-semibold text-purple-700 border-l-4 border-purple-500 pl-2">
                    Platform & Activity
                  </h3>

                  {/* Login Info */}
                  <p>
                    <strong>Logged In:</strong>{" "}
                    {viewModal.user?.is_logged_in ? "Yes" : "No"}
                  </p>
                  <p>
                    <strong>Login Source:</strong>{" "}
                    {viewModal.user?.login_source || "N/A"}
                  </p>
                  <p>
                    <strong>Last Visited:</strong>{" "}
                    {formatDateTimeAmORPm(viewModal.user?.last_visited) ||
                      "N/A"}
                  </p>
                  <p>
                    <strong>Created At:</strong>{" "}
                    {formatDateTimeAmORPm(viewModal.user?.created_at)}
                  </p>
                  <p>
                    <strong>Updated At:</strong>{" "}
                    {formatDateTimeAmORPm(viewModal.user?.updated_at)}
                  </p>
                </div>
              )}

              {/* 🟧 EVENT ACTIVITY TAB */}
              {activeUserTab === "Event Activity" && (
                <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="text-base font-semibold text-orange-700 border-l-4 border-orange-500 pl-2">
                      Event Activity
                    </h3>
                    <button
                      onClick={handleExportUserEventActivityCSV}
                      disabled={
                        eventActivityExporting ||
                        eventActivityLoading ||
                        !viewModal.user?.id
                      }
                      title="Export Event Activity CSV"
                      className={`p-2 rounded shadow-md flex items-center justify-center ${
                        eventActivityExporting ||
                        eventActivityLoading ||
                        !viewModal.user?.id
                          ? "bg-gray-400 cursor-not-allowed"
                          : "bg-green-500 hover:opacity-90 text-white"
                      }`}
                    >
                      {eventActivityExporting ? (
                        <svg
                          className="animate-spin h-4 w-4 text-white"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                            fill="none"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                          />
                        </svg>
                      ) : (
                        React.createElement(FaFileCsv as any, {
                          className: "w-4 h-4",
                        })
                      )}
                    </button>
                  </div>

                  {!eventActivityLoading && eventActivity.length === 0 ? (
                    <div className="text-center text-gray-500 py-6 min-h-[50vh] flex items-center justify-center">
                      No event activity found for this user.
                    </div>
                  ) : (
                    <div
                      className={`relative transition-opacity ${
                        eventActivityLoading
                          ? "opacity-60 pointer-events-none"
                          : ""
                      }`}
                    >
                      {eventActivityLoading && (
                        <div className="absolute inset-0 z-10 flex items-center justify-center text-gray-600 bg-white/40 rounded-lg">
                          Loading event activity...
                        </div>
                      )}
                      <div className="overflow-x-auto bg-white border border-orange-200 rounded-lg h-[50vh]">
                        <table className="min-w-full text-xs text-left">
                          <thead className="bg-orange-100 text-orange-800 uppercase sticky top-0">
                            <tr>
                              <th className="px-3 py-2">Event ID</th>
                              <th className="px-3 py-2">Event Name</th>
                              <th className="px-3 py-2">RSVP</th>
                              <th className="px-3 py-2 text-right">Total Tickets</th>
                              <th className="px-3 py-2 text-right">Refunded Tickets</th>
                              <th className="px-3 py-2 text-right">Purchased Amount</th>
                              <th className="px-3 py-2 text-right">Refunded Amount</th>
                              <th className="px-3 py-2 text-right">Socha Fee</th>
                            </tr>
                          </thead>
                          <tbody>
                            {eventActivity.map((row) => (
                              <tr
                                key={row.eventId}
                                className="border-t border-orange-100 hover:bg-orange-50"
                              >
                                <td className="px-3 py-2">{row.eventId}</td>
                                <td className="px-3 py-2">{row.eventName}</td>
                                <td className="px-3 py-2">
                                  <div className="flex items-center gap-1.5">
                                    {row.isHost ? (
                                      <span
                                        title="Host of this event"
                                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-800"
                                      >
                                        {React.createElement(Crown as any, {
                                          className: "w-3 h-3",
                                        })}
                                        Host
                                      </span>
                                    ) : (
                                      <span
                                        className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                                          row.rsvpStatus === "Yes"
                                            ? "bg-green-100 text-green-700"
                                            : row.rsvpStatus === "No"
                                            ? "bg-red-100 text-red-700"
                                            : "bg-gray-100 text-gray-700"
                                        }`}
                                      >
                                        {row.rsvpStatus || "N/A"}
                                      </span>
                                    )}
                                  </div>
                                </td>
                                <td className="px-3 py-2 text-right">
                                  {row.totalTickets}
                                </td>
                                <td className="px-3 py-2 text-right">
                                  {row.refundedTickets}
                                </td>
                                <td className="px-3 py-2 text-right">
                                  ${Number(row.purchasedAmount || 0).toFixed(2)}
                                </td>
                                <td className="px-3 py-2 text-right">
                                  ${Number(row.refundedAmount || 0).toFixed(2)}
                                </td>
                                <td className="px-3 py-2 text-right">
                                  ${Number(row.sochaFee || 0).toFixed(2)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {/* Pagination */}
                      <div className="flex items-center justify-between pt-2 text-xs text-gray-700">
                        <span>
                          Showing page{" "}
                          <strong>{eventActivityPage}</strong> of{" "}
                          <strong>{eventActivityTotalPages}</strong>{" "}
                          ({eventActivityTotalCount} total)
                        </span>
                        <div className="flex gap-2">
                          <button
                            onClick={() =>
                              setEventActivityPage((p) => Math.max(1, p - 1))
                            }
                            disabled={
                              eventActivityLoading || eventActivityPage <= 1
                            }
                            className="px-3 py-1 rounded border border-orange-300 bg-white text-orange-700 hover:bg-orange-100 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Prev
                          </button>
                          <button
                            onClick={() =>
                              setEventActivityPage((p) =>
                                Math.min(eventActivityTotalPages, p + 1)
                              )
                            }
                            disabled={
                              eventActivityLoading ||
                              eventActivityPage >= eventActivityTotalPages
                            }
                            className="px-3 py-1 rounded border border-orange-300 bg-white text-orange-700 hover:bg-orange-100 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Next
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex justify-end mt-6"></div>
          </div>
        </div>
      )}
    </Layout>
  );
}
